/**
 * CORE-745 — how an anonymous visitor gets into a Live-практика.
 *
 * Pure, no RN deps, so it's unit-testable in the node jest env (same reason
 * `calendarRooms.ts` / `calendarSpan.ts` live here rather than inside the
 * screens that use them).
 *
 * The announcement page used to offer a guest exactly one pre-start action —
 * «Присоединиться» — wired to the RSVP handler. RSVP needs an account, so the
 * handler bounced straight to `/login?returnTo=/room/{id}`, and there was no
 * other way in: the non-auth destination (`handleJoinRoom`) only existed
 * inside the 30-second join window. Guest entry — the primary product
 * scenario for Live-практики — was unreachable before the room opened.
 *
 * The backend was never the blocker: `/api/watch-parties/{code}`,
 * `/api/watch-parties/id/{id}`, `/{code}/spectate` and `/{id}/spectate/rtc-token`
 * all answer 200 without a token (measured on dev 2026-08-04). The guest RTC
 * token comes back `role: "subscriber"` / `canPublish: false`, which is why a
 * guest enters as a viewer and the green room says so.
 */

import { FALLBACK_GUEST } from './displayName';

/** Mirrors the `maxLength` on the green-room name input. */
export const GUEST_NAME_MAX = 40;

/**
 * The name a guest typed, normalised for display: whitespace collapsed,
 * trimmed, clipped. Returns '' for anything that carries no name at all, so
 * callers can treat empty as «guest didn't choose one».
 */
export const sanitizeGuestName = (raw: string | null | undefined): string =>
  (raw ?? '').replace(/\s+/g, ' ').trim().slice(0, GUEST_NAME_MAX);

/**
 * Display name for a spectator in the room and on the SignalR hub.
 *
 * `/spectate` is a GET with no body, so the backend always mints its own
 * random handle («Gentle Wren»). The name the guest chose in the green room
 * only reaches the room because the hub invoke carries it
 * (`SpectateParty(partyId, spectatorId, spectatorName)`), so it has to be
 * substituted here rather than server-side.
 */
export const resolveSpectatorName = (
  chosen: string | null | undefined,
  fallback: string,
): string => sanitizeGuestName(chosen) || fallback;

/** A viewer the room knows by name — id from the hub, name as broadcast. */
export interface RoomSpectator {
  spectatorId: string;
  name: string;
}

export interface SpectatorRow extends RoomSpectator {
  isSelf: boolean;
  /**
   * CORE-1197 — этот зритель УЖЕ стоит в ростере участников.
   *
   * Так бывает в комнате «говорят все»: гость получает право публиковать по
   * одному факту входа, попадает в ростер и остаётся при этом зрителем в
   * гостевом хеше — то есть виден в обоих списках. Ведущему в такой строке
   * предлагать «Дать слово» бессмысленно: оно у него уже есть, а отзывается
   * с карточки в ростере.
   */
  inRoster: boolean;
}

/**
 * CORE-745 QA fail — the viewer rows for the participants panel.
 *
 * The name a guest types travelled all the way to the server (measured on dev:
 * `SpectateParty(id, sid, name)` comes back to every client as
 * `SpectatorJoined { spectatorId, name, spectatorCount }`), but no surface ever
 * rendered it: the panel drew a bare «Смотрят без аккаунта: N» footer and
 * spectators are absent from the BE participant roster entirely. So the guest
 * saw no trace of the name they chose, and neither did the host — which reads
 * exactly like «имя не передаётся в сессию».
 *
 * Two sources merge here because neither alone is complete:
 *   - `self` — what *we* typed. Always first, always present, even before our
 *     own `SpectatorJoined` echo lands (and after a reconnect drops the list).
 *   - `fromHub` — everyone this client knows about: since CORE-1197 the server
 *     sends a `SpectatorSnapshot` on hub join, so the list survives a reconnect
 *     instead of rebuilding itself only out of arrivals we happened to witness.
 *     Anything the snapshot still cannot account for (a spectator on a server
 *     that predates it) is reported as `unnamedCount` rather than silently
 *     dropped or padded with fake rows.
 *
 * `rosterIds` marks the viewers who are also participants. The row is where the
 * host reaches a guest who is NOT one — in a «после „Дать слово"» room that is
 * the only place the floor can be handed over — so the two cases have to be
 * distinguishable here rather than at the render site.
 *
 * `rosterGuestIds` — те же строки ростера, но ТОЛЬКО гостевые, и нужны они
 * одной арифметике: сколько посчитанных сервером зрителей уже показано выше.
 * Список участников с аккаунтом сюда класть нельзя — они в `spectatorCount` не
 * входят, и вычитание увело бы счётчик в минус.
 */
export const mergeSpectators = (
  fromHub: readonly RoomSpectator[],
  self: RoomSpectator | null,
  totalCount: number,
  rosterIds: readonly string[] = [],
  rosterGuestIds: readonly string[] = [],
): { rows: SpectatorRow[]; unnamedCount: number } => {
  const rows: SpectatorRow[] = [];
  const seen = new Set<string>();
  const inRosterSet = new Set(rosterIds);
  const push = (s: RoomSpectator, isSelf: boolean) => {
    if (!s.spectatorId || seen.has(s.spectatorId)) return;
    seen.add(s.spectatorId);
    rows.push({
      spectatorId: s.spectatorId,
      name: sanitizeGuestName(s.name) || FALLBACK_GUEST,
      isSelf,
      inRoster: inRosterSet.has(s.spectatorId),
    });
  };
  if (self) push(self, true);
  for (const s of fromHub) push(s, false);
  // CORE-1197 (замер staging 12.09.2026) — гость, стоящий в РОСТЕРЕ, но не
  // известный этому клиенту по имени, считался дважды: один раз строкой
  // ростера, второй — безымянным хвостом.
  //
  // Так выходит у всякого, кто вошёл в комнату позже говорящего гостя: список
  // зрителей собирается из кадров `SpectatorJoined`, а их приходят только про
  // тех, кто вошёл ПОСЛЕ нас (сервер не шлёт входящему ни `SpectatorSnapshot`,
  // ни `CurrentParticipants` — спека `docs/backend/live-guest-visibility-CORE-1197.md`).
  // Замер комнаты WYcD9v: у ГОСТЯ-3 ростер=3, известных зрителей=4,
  // spectatorCount=6 — счётчик показывал 3+4+2=9 человек при семи реальных.
  //
  // Такой гость сервером посчитан и уже виден строкой, поэтому из хвоста он
  // вычитается наравне с названными. Пустой список — прежнее поведение:
  // недосчитать людей хуже, чем посчитать лишнего.
  const countedByRoster = rosterGuestIds.filter(id => !!id && !seen.has(id)).length;
  return { rows, unnamedCount: Math.max(0, totalCount - rows.length - countedByRoster) };
};

/**
 * CORE-1358 — строки футера «Слушатели»: только те, кого НЕТ в ростере.
 *
 * Гость со словом стоит в ростере (у него «Со словом») и в гостевом хеше
 * сервера одновременно, и до этого футер показывал его второй раз, уже
 * «Слушателем» — один человек в двух взаимоисключающих группах (QA
 * 09.09.2026). `unnamedCount` из `mergeSpectators` при этом не меняется:
 * такой гость сервером посчитан и по имени известен, просто показан выше.
 */
export const spectatorListenerRows = (rows: readonly SpectatorRow[]): SpectatorRow[] =>
  rows.filter(r => !r.inRoster);

/** Что ведущему можно сделать со строкой зрителя. */
export interface SpectatorRowActions {
  showGrantFloor: boolean;
  showRemove: boolean;
}

/**
 * CORE-1300 — какие действия несёт строка гостя в панели участников.
 *
 * До этого строка умела ровно одно — «Дать слово» (CORE-1197), — и исключить
 * гостя ведущему было нечем: карточки участника у зрителя нет, а никакого
 * другого места, где он вообще виден, тоже нет. Серверный путь при этом есть с
 * CORE-1199: `KickParticipant` первой веткой обрабатывает именно гостя, и
 * `targetUserId` там несёт `spectatorId`. То есть чинилась не сеть и не
 * контракт, а отсутствие кнопки: команда, которой нет в интерфейсе, до сервера
 * не доходит.
 *
 * Два права разведены намеренно. `canModerateAudio` — про слово, `canKick` —
 * про исключение, и на сервере это тоже разные вещи (ветка гостя стоит ПОСЛЕ
 * `IsHostAsync`, права у кика гостя ровно те же, что у кика участника). Обещать
 * действие, которое хаб отобьёт «Only the host can kick participants», нельзя.
 *
 * `inRoster` гасит только «Дать слово»: гость, у которого слово уже есть, стоит
 * и в ростере, и в списке зрителей — предлагать ему слово нечего, а исключать
 * его можно с обеих строк, серверу разницы нет.
 */
export const resolveSpectatorRowActions = (
  row: Pick<SpectatorRow, 'isSelf' | 'inRoster'>,
  perms: { canModerateAudio: boolean; canKick: boolean },
): SpectatorRowActions => ({
  showGrantFloor: perms.canModerateAudio && !row.isSelf && !row.inRoster,
  showRemove: perms.canKick && !row.isSelf,
});

/** Что green room говорит гостю о его роли в сессии. */
export interface GuestEntryCopy {
  /** Подпись кнопки входа. */
  enterLabel: string;
  /** Строка под полем имени: что гостю будет доступно. */
  note: string;
}

/**
 * CORE-1197 (возврат QA 08.09.2026) — подписи green room для гостя.
 *
 * До этого экран говорил каждому гостю «Войти как зритель» и «Чтобы участвовать
 * голосом — войдите в аккаунт», хотя с CORE-1197/1199 гость в комнате «говорят
 * все» получает микрофон с порога, а в комнате «после „Дать слово"» — по слову
 * ведущего. Обещание «зритель» над работающим микрофоном читается как дефект
 * ровно так же, как кнопка над молчащим соединением, — просто с другой стороны.
 *
 * Решает не клиент: `policy` — это ответ сервера из `/spectate`
 * (`resolveGuestPolicy`). `null` — политику не прочли (старый стенд, сбой
 * сети), и тогда подписи остаются зрительскими, как до этой правки: обещать
 * микрофон, не зная, дадут ли его, нельзя.
 *
 * Чат гостю закрыт при любой политике (`room.spectator.signInToChat`), поэтому
 * про чат сказано во всех трёх вариантах.
 */
export const resolveGuestEntryCopy = (
  policy: { canPublish: boolean; camerasAllowed: boolean } | null,
): GuestEntryCopy => {
  // CORE-1860 (QA FAIL 02.11) — «слова» больше нет: доступ гостя решают
  // разрешения устройств, которые ведущий включает для всей комнаты, и гость
  // перечитывает их уже в комнате (`resolveGuestPolicy`). Политику не прочли —
  // о микрофоне и камере не обещаем ничего ни в одну сторону.
  if (!policy) {
    return {
      enterLabel: 'Войти как зритель',
      note: 'Вы войдёте как зритель. Чтобы писать в чат — войдите в аккаунт.',
    };
  }
  if (!policy.canPublish) {
    return {
      enterLabel: 'Войти как зритель',
      note: 'Вы войдёте как зритель: микрофон и камера сейчас недоступны. Если ведущий их разрешит, вы сможете включить их в Live-сессии. Чтобы писать в чат — войдите в аккаунт.',
    };
  }
  return {
    enterLabel: 'Войти в Live-сессию',
    note: policy.camerasAllowed
      ? 'Микрофон и камера будут доступны. Чтобы писать в чат — войдите в аккаунт.'
      : 'Микрофон будет доступен, камеры в этой сессии выключены. Чтобы писать в чат — войдите в аккаунт.',
  };
};

/**
 * CORE-1860 (QA FAIL 02.11) — булев параметр маршрута. SPA-переход передаёт
 * настоящий boolean, а reload веба разбирает адрес, и каждый флаг приходит
 * строкой: `"false"` истинно для `!!`. Истиной считаются только `true` и `"true"`.
 */
export const isTrueRouteParam = (value: unknown): boolean =>
  value === true || value === 'true';

export type RoomEntryRoute = 'LiveSessionEntry' | 'LiveRoomSession';

/**
 * Which screen a room-entry press lands on.
 *
 * Guests ALWAYS go through the green room: it is the only surface that asks
 * for a display name and checks camera/mic, and the legacy (flag-off) path
 * has no guest equivalent — it drops them straight into the broadcast under a
 * backend-generated handle. Authenticated users keep the flag-driven
 * behaviour untouched, so nothing regresses while `unified_live_session` is
 * still off.
 */
export const resolveRoomEntryRoute = (opts: {
  unifiedLive: boolean;
  isAuthenticated: boolean;
}): RoomEntryRoute =>
  (opts.unifiedLive || !opts.isAuthenticated) ? 'LiveSessionEntry' : 'LiveRoomSession';
